import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { config } from '../config';
import { getLocalStorageItem } from '../utils/localStorage';

// Socket.IO connection options with robust reconnection settings
const SOCKET_OPTIONS = {
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 2000,
  reconnectionDelayMax: 10000,
  timeout: 20000,
  autoConnect: false, // Changed to false - we'll manually connect after checking consent
  transports: ['websocket', 'polling'],
  // Set ping interval longer for fewer reconnections
  pingInterval: 25000,
  pingTimeout: 10000
};

/**
 * A custom hook for managing Socket.IO connections with reconnection handling
 * 
 * Features:
 * - Automatic connection to the configured API URL
 * - Robust reconnection strategy with exponential backoff
 * - Connection status tracking
 * - Clean disconnection on component unmount
 * - Retry limit to prevent infinite reconnection attempts
 * - Respects user's localStorage consent
 * 
 * @returns An object containing the Socket.IO instance and connection status
 */
export const useSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [connectionError, setConnectionError] = useState<Error | null>(null);
  
  // Use a ref to maintain socket instance across renders
  const socketRef = useRef<Socket | null>(null);
  
  // Reconnect manually if needed
  const reconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.connect();
    }
  }, []);

  useEffect(() => {
    // Check if the user has consented to localStorage
    const hasConsented = getLocalStorageItem<string>('localStorage_consent', 'false') === 'true';

    // Initialize Socket.IO with reconnection settings
    const socket = io(config.api.baseUrl, SOCKET_OPTIONS);
    socketRef.current = socket;

    // Connection event handlers
    const onConnect = () => {
      setIsConnected(true);
      setConnectionError(null);
      setReconnectAttempts(0);
    };

    const onDisconnect = (reason: string) => {
      setIsConnected(false);
      
      // If the server initiated the disconnect, don't auto-reconnect
      if (reason === 'io server disconnect') {
        socket.connect();
      }
    };

    const onReconnectAttempt = (attempt: number) => {
      setReconnectAttempts(attempt);
    };

    const onReconnectFailed = () => {
      // Don't automatically retry after max attempts to avoid infinite loops
      // User can manually trigger reconnection if needed
    };

    const onError = (error: Error) => {
      setConnectionError(error);
    };

    // Listen for consent changes
    const handleConsentChanged = () => {
      if (socket && !socket.connected) {
        socket.connect();
      }
    };

    // Register event handlers
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('reconnect_attempt', onReconnectAttempt);
    socket.on('reconnect_failed', onReconnectFailed);
    socket.on('error', onError);
    
    // Add listener for the custom consent event
    window.addEventListener('localStorageConsentChanged', handleConsentChanged);

    // Only connect if user has consented to localStorage
    if (hasConsented) {
      // Connect only after all event handlers are set up
      socket.connect();
    }

    // Clean up on unmount
    return () => {
      if (socket) {
        socket.off('connect', onConnect);
        socket.off('disconnect', onDisconnect);
        socket.off('reconnect_attempt', onReconnectAttempt);
        socket.off('reconnect_failed', onReconnectFailed);
        socket.off('error', onError);
        socket.disconnect();
      }
      window.removeEventListener('localStorageConsentChanged', handleConsentChanged);
    };
  }, []);

  return {
    socket: socketRef.current,
    isConnected,
    reconnectAttempts,
    connectionError,
    reconnect,
  };
}; 